import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import SEOHead from '@/components/base/SEOHead';
import { comics, categories, faqs } from '@/mocks/comics';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const hotComics = comics.filter((c) => c.rating >= 9.5).slice(0, 8);
  const latestComics = [...comics].sort((a, b) => b.views - a.views).slice(0, 8);
  const featuredComics = comics.slice(0, 4);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredComics.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [featuredComics.length]);

  const schemaJson = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩漫画网',
    url: 'https://www.hanmanhua.com',
    description: '韩漫画网设有免费漫画弹窗入口，作为海量正版漫画在线阅读平台，内容安全可靠，让您安心享受高质量漫画资源。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.hanmanhua.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <SEOHead
        title="韩漫画网-免费漫画弹窗入口-海量正版漫画在线阅读平台"
        description="韩漫画网设有免费漫画弹窗入口，作为海量正版漫画在线阅读平台，内容安全可靠，让您安心享受高质量漫画资源。涵盖恋爱、奇幻、动作、悬疑等多种类型的韩国漫画，每日更新，免费阅读。"
        keywords="韩漫画网, 免费漫画弹窗入口, 海量正版漫画在线阅读平台, 韩国漫画, 在线漫画, 免费漫画"
        canonicalUrl="https://www.hanmanhua.com"
        schemaJson={schemaJson}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative h-[420px] md:h-[600px] w-full overflow-hidden">
            {featuredComics.map((comic, index) => (
              <div
                key={comic.id}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <img
                  src={comic.cover}
                  alt={comic.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50"></div>
              </div>
            ))}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4 max-w-3xl">
                海量正版韩漫，免费在线阅读
              </h1>
              <p className="text-sm md:text-lg text-background-100/90 mb-8 max-w-2xl leading-relaxed">
                韩漫画网设有<strong>免费漫画弹窗入口</strong>，作为海量正版漫画在线阅读平台，内容安全可靠，让您安心享受高质量漫画资源。
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <Link
                  to="/category/lianai"
                  className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-base font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  免费漫画弹窗入口
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3 bg-background-50/20 text-background-50 border border-background-50/30 rounded-full text-base font-medium hover:bg-background-50/30 transition-all whitespace-nowrap cursor-pointer"
                >
                  了解更多
                </Link>
              </div>
              <div className="flex items-center gap-2 mt-8">
                {featuredComics.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${
                      index === currentSlide ? 'bg-primary-500 w-8' : 'bg-background-50/50 hover:bg-background-50/80'
                    }`}
                    aria-label={`切换到第${index + 1}张`}
                  ></button>
                ))}
              </div>
            </div>
          </section>

          <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
              {categories.slice(0, 8).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center p-5 md:p-6 rounded-xl bg-background-50 border border-background-200 hover:border-primary-300 transition-all cursor-pointer"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all mb-3">
                    <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-800 whitespace-nowrap">{cat.name}</span>
                  <span className="text-xs text-foreground-500 mt-1 text-center line-clamp-1">{cat.description}</span>
                </Link>
              ))}
            </div>
          </section>

          <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-10 md:pb-14">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">热门推荐</h2>
                <p className="text-sm text-foreground-500 mt-1">评分最高的漫画作品，不容错过</p>
              </div>
              <Link
                to="/category/juqing"
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
              {hotComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </section>

          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">免费漫画弹窗入口</h2>
                <p className="text-sm text-foreground-500 mt-2">无需付费，点击即看，每日更新热门漫画免费章节</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {comics.filter((c) => c.chapters.some((ch) => ch.isFree)).slice(0, 4).map((comic) => (
                  <div key={comic.id} className="bg-background-50 rounded-xl border border-background-200 overflow-hidden group cursor-pointer">
                    <div className="flex items-start gap-4 p-4">
                      <div className="w-20 h-28 flex-shrink-0 rounded-lg overflow-hidden">
                        <img src={comic.cover} alt={comic.title} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1">{comic.title}</h3>
                        <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                        <div className="flex items-center gap-1 mt-2">
                          <span className="text-xs text-accent-600 font-medium bg-accent-100 px-2 py-0.5 rounded-full">免费章节</span>
                        </div>
                        <p className="text-xs text-foreground-400 mt-2 line-clamp-2">{comic.description}</p>
                        <Link
                          to={`/article/${comic.id}`}
                          className="inline-flex items-center gap-1 text-xs text-primary-600 font-medium mt-2 hover:text-primary-700 cursor-pointer"
                        >
                          立即阅读 <i className="ri-arrow-right-s-line"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">最新更新</h2>
                <p className="text-sm text-foreground-500 mt-1">每日更新，精彩不断</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
              {latestComics.map((comic) => (
                <ComicCard key={`latest-${comic.id}`} {...comic} />
              ))}
            </div>
          </section>

          <section className="bg-background-100 py-10 md:py-14" id="faq">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">常见问题</h2>
                <p className="text-sm text-foreground-500 mt-2">关于韩漫画网的使用指南</p>
              </div>
              <div className="max-w-3xl mx-auto space-y-3">
                {faqs.map((faq, index) => (
                  <details key={index} className="group bg-background-50 rounded-xl border border-background-200 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 group-open:bg-primary-100 group-open:text-primary-600 transition-all">
                        <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform"></i>
                      </span>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14 text-center">
            <div className="bg-primary-500 rounded-2xl p-8 md:p-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-3">
                立即开启您的韩漫之旅
              </h2>
              <p className="text-sm md:text-base text-background-100/90 mb-6 max-w-xl mx-auto">
                韩漫画网设有免费漫画弹窗入口，海量正版漫画等您来读。无需等待，马上开始阅读！
              </p>
              <Link
                to="/category/lianai"
                className="inline-flex items-center gap-2 px-8 py-3 bg-background-50 text-primary-600 rounded-full text-base font-semibold hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
              >
                免费漫画弹窗入口 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </section>
        </main>

        <Footer />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: { '@type': 'Answer', text: faq.answer },
              })),
            }),
          }}
        />
      </div>
    </>
  );
}